import { DismissCountBanners } from '@/constants/closedBanners';
import dashboardPageSlice, { makeBannerDismissedSelector } from '@/redux/features/dashboard.slice';
import dashboardSessionSlice, { makeBannerSessionHiddenSelector } from '@/redux/features/dashboardSession.slice';
import { Icon } from '@shopify/polaris';
import { XIcon } from '@shopify/polaris-icons';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

const STARS = [1, 2, 3, 4, 5];
// BFS TR-444: the rating stars must render as solid yellow with no contrasting
// outline (PO sign-off on Image #2). Sharing the same colour between `fill` and
// `stroke` keeps filled stars border-less and unfilled stars with a faint yellow
// hint. Extracted to a constant so any future "make them look pop" tweak surfaces
// as an explicit code review, instead of a stray colour swap on the SVG path.
const STAR_COLOR = '#FFC200';

const BannerWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--p-color-bg-surface);
  border: 1px solid var(--p-color-border-secondary);
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    flex-wrap: wrap;
    align-items: flex-start;
  }
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  background: #fff0f1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  .title {
    font-size: 14px;
    font-weight: 600;
    color: #202223;
    margin-bottom: 2px;
  }
  .subtitle {
    font-size: 13px;
    color: #6d7175;
    line-height: 1.4;
  }
`;

const RightWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const Stars = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const StarBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  line-height: 0;
  transition: transform 0.1s;
  &:hover {
    transform: scale(1.15);
  }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  line-height: 0;
  border-radius: 4px;
  color: #8c9196;
  &:hover {
    background: #f6f6f7;
  }

  @media (max-width: 768px) {
    position: absolute;
    top: 8px;
    right: 8px;
  }
`;

const REVIEW_BANNER_DISMISS_THRESHOLD = 3;

const ReviewBanner = () => {
  const reviewBannerDismissedSelector = useMemo(
    () => makeBannerDismissedSelector(DismissCountBanners.ReviewBanner, REVIEW_BANNER_DISMISS_THRESHOLD),
    [],
  );
  const reviewBannerSessionHiddenSelector = useMemo(() => makeBannerSessionHiddenSelector(DismissCountBanners.ReviewBanner), []);
  const permanentlyDismissed = useSelector(reviewBannerDismissedSelector);
  const sessionHidden = useSelector(reviewBannerSessionHiddenSelector);
  const dispatch = useDispatch();
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);

  if (permanentlyDismissed || sessionHidden) return null;

  const handleDismiss = () => {
    dispatch(dashboardSessionSlice.actions.hideBannerForSession(DismissCountBanners.ReviewBanner));
    dispatch(dashboardPageSlice.actions.incrementBannerCloseCount(DismissCountBanners.ReviewBanner));
  };

  const handleStarClick = async () => {
    // Every rating routes to the same App Store review request — no gating by
    // star count. Permanently dismiss the banner once the user has rated.
    for (let i = 0; i < REVIEW_BANNER_DISMISS_THRESHOLD; i++) {
      dispatch(dashboardPageSlice.actions.incrementBannerCloseCount(DismissCountBanners.ReviewBanner));
    }
    try {
      await (shopify as any)?.reviews?.request();
    } catch {
      window.open('https://apps.shopify.com/omega-order-tracking?reveal_new_review=true', '_blank');
    }
  };

  return (
    <BannerWrap>
      <IconWrap>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path
            d="M12 21C12 21 3 14 3 8.5C3 6.01 5.01 4 7.5 4C9.24 4 10.91 5.01 12 6.5C13.09 5.01 14.76 4 16.5 4C18.99 4 21 6.01 21 8.5C21 14 12 21 12 21Z"
            fill="#F76C82"
            stroke="#F76C82"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </IconWrap>

      <Content>
        <div className="title">How is your experience so far?</div>
        <div className="subtitle">
          We're grateful to support your store. Every review and suggestion reminds our team why we do this.
        </div>
      </Content>

      <RightWrap>
        <Stars>
          {STARS.map((star) => {
            const active = star <= (hoveredStar ?? 5);
            return (
              <StarBtn
                key={star}
                onMouseEnter={() => setHoveredStar(star)}
                onMouseLeave={() => setHoveredStar(null)}
                onClick={() => handleStarClick()}
                aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    fill={active ? STAR_COLOR : 'none'}
                    stroke={STAR_COLOR}
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </StarBtn>
            );
          })}
        </Stars>
      </RightWrap>

      <CloseBtn onClick={handleDismiss} aria-label="Dismiss">
        <Icon source={XIcon} />
      </CloseBtn>
    </BannerWrap>
  );
};

export default ReviewBanner;
